import { PanelType, SelectionMode } from '@fluentui/react';
import { useBoolean, useConst } from '@fluentui/react-hooks';
import { ChoiceGroup, Label } from 'office-ui-fabric-react';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Scriba from 'react-scriba';
import { NotificationsTarget, useNotificationsManager } from '../../contexts/notificationContext';
import { Data } from '../../data/Data';
import { DataMandat } from '../../data/DataMandat.jsx';
import { IDS_TYPE_MANDAT } from '../../data/DataTypeMandat.jsx';
import { Immocom } from '../../specifique/immocom';
import { ComboBoxAgence } from '../ComboBox/ComboBoxAgence.jsx';
import { ComboBoxCompte } from '../ComboBox/ComboBoxCompte.jsx';
import { ComboBoxNiveau, IDS_NIVEAU } from '../ComboBox/ComboBoxNiveau';
import { ComboBoxOperation } from '../ComboBox/ComboBoxOperation.jsx';
import { ComboBoxProduit } from '../ComboBox/ComboBoxProduit';
import { ComboBoxRegion } from '../ComboBox/ComboBoxRegion.jsx';
import { ComboBoxSociete } from '../ComboBox/ComboBoxSociete';
import { ComboBoxStatutMandat } from '../ComboBox/ComboBoxStatutMandat';
import { ComboBoxTypeMandat } from '../ComboBox/ComboBoxTypeMandat.jsx';
import { EUIDatePicker } from '../EUI/EUIDatePicker';
import { EUINumericField } from '../EUI/EUINumericField';
import { EUIPanel } from '../EUI/EUIPanel.jsx';
import { EUIToggle } from '../EUI/EUIToggle.jsx';
import DetailsListNiveauAlerte from '../List/DetailsListAlerte';
import DetailsListLot from '../List/DetailsListLot';
import TextFieldHonoraires from '../TextField/TextFieldHonoraires';

// KP - 2334 - 10/03/2023 - Migration des ComboBox en EUIComboBox ; bascule sur ComboBoxAgence / Region / Operation
// KP - 2550 - 09/10/2023 - MàJ types mandats à exclure et clean code
// KP - 2625 - 12/03/2024 - Correction bug + migration FC

const FicheMandat = (props) => {

    // CONTEXTS
    const { clear, dismiss, extract, load } = useNotificationsManager();

    // PROPS
    const {
        idMandat = undefined,
        idTypeMandat = undefined,
        isOpen = false,
        typeDeMandat = undefined,
        /* --- */
        onDismiss = () => null,
        onSave = () => null
    } = props;

    // CONSTANTES
    const { NATIONAL, REGION, AGENCE, OPERATION, LOT } = IDS_NIVEAU;

    const defaultMandat = useConst({
        baseCalculHonoraireCommercialisation: "HT sur TTC",
        idAgence: 0,
        idRegion: 0,
        idStatutMandat: 4, // KP - 895 - 20/05/2020 : statut actif par défaut
        idTypeMandat: 0,
        mandatToutProduit: true,
    });

    // STATES
    const [_isFormValid, { setFalse: _setFormIsNotValid, setTrue: _setFormIsValid }] = useBoolean(false);

    const [_estAdminNational, _setEstAdminNational] = useState(false);
    const [_idNiveauSelectionne, _setIdNiveauSelectionne] = useState("0");
    const [_listeDesAlertesSelectionnees, _setListeDesAlertesSelectionnees] = useState([]);
    const [_lotsSelectionnes, _setLotsSelectionnes] = useState([]); // Liste des lots sélectionnés récupérés lors du click dans DetailsListLots
    const [_mandat, _setMandat] = useState({ ...defaultMandat });

    // REFS
    const _isLoaded = useRef(false); // Utilisé pour économiser des vérifications de formulaire
    const _isMounted = useRef(false); // Utilisé pour bloquer certains useEffect au premier render

    // MEMOS
    const _headerText = useMemo(() => {
        let headerText = !idMandat ? "Nouveau mandat " : "Modification de mandat ";
        headerText += (() => {
            switch (_mandat.idTypeMandat) {
                case 0: return "Autre"; break;
                case IDS_TYPE_MANDAT.OPERATIONNEL: return "Opérationnel"; break;
                case IDS_TYPE_MANDAT.COMMERCIALISATEUR: return "Commercialisateur"; break;
                case IDS_TYPE_MANDAT.CONTROLE_DE_GESTION: return "Contrôle de gestion"; break;
                case IDS_TYPE_MANDAT.GESTIONNAIRE_MANDAT: return "Gestionnaire de mandat"; break;
                case IDS_TYPE_MANDAT.WEB_SERVICES: return "Web Services"; break;
                case IDS_TYPE_MANDAT.ADMINISTRATEUR: return "Administrateur"; break;
                case IDS_TYPE_MANDAT.DIRECTEUR_COMMERCIAL: return "Directeur commercial"; break;
                case IDS_TYPE_MANDAT.LECTEUR: return "Lecteur"; break;
                case IDS_TYPE_MANDAT.MAISON_INDIVIDUELLE: return "Maison individuelle"; break;
                case IDS_TYPE_MANDAT.EBOOK: return "EBook"; break;
                default: return ""; break;
            }
        })();
        return headerText;
    }, [_mandat.idTypeMandat, idMandat]);

    const _isDisabledComboBoxTypeMandat = useMemo(() => {
        return !!idMandat
            || _mandat.idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR
            || _mandat.idTypeMandat === IDS_TYPE_MANDAT.WEB_SERVICES
    }, [_mandat.idTypeMandat, idMandat]);

    const _isDisabledComboBoxProduit = useMemo(() => {
        return !!idMandat // Le champs ComboBoxProduit est désactivé dans le cas d'une mise à jour
            // ou dans le cas d'une création s'il s'agit d'un mandat autre que "commercialisateur" ou "web-services"
            || (!idMandat && _mandat.idTypeMandat !== IDS_TYPE_MANDAT.COMMERCIALISATEUR && _mandat.idTypeMandat !== IDS_TYPE_MANDAT.WEB_SERVICES)
    }, [_mandat.idTypeMandat, idMandat]);

    const _mandatObject = useMemo(() => {
        const mandat = {
            ..._mandat,
            idNiveauSelectionne: _idNiveauSelectionne,
            lotsSelectionnes: _lotsSelectionnes
        };
        return mandat;
    }, [_idNiveauSelectionne, _lotsSelectionnes, _mandat]);

    // METHODS
    const _chargementDonnees = useCallback(() => {
        const id = load("Chargement du mandat en cours...");
        DataMandat._chargementFicheMandat(idMandat).then(response => {
            const onSuccess = () => {

                const data = response.reponseAPIobjet || {};
                const { estAdminNational } = data;

                // Création
                let newMandat = {
                    ...defaultMandat,
                    idTypeMandat,
                    mandatToutProduit: idTypeMandat === 0
                };

                // Modification
                if (!!idMandat) {
                    const { idNiveauSelectionne, listMandatLotCommercial, listMandatNiveauAlerte, mandat } = data;
                    newMandat = mandat;
                    // KP - 2625 - 22/03/2024 : ajout valeurs par défaut
                    if (newMandat.idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR) {
                        newMandat.dureeOption |= 0;
                        newMandat.nbreOptionParOperation |= 0;
                    }
                    _setIdNiveauSelectionne(idNiveauSelectionne);
                    _setListeDesAlertesSelectionnees(listMandatNiveauAlerte);
                    _setLotsSelectionnes(listMandatLotCommercial);
                }

                _setEstAdminNational(estAdminNational);
                _isLoaded.current = true;
                _setMandat(newMandat);

            };
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [idMandat, idTypeMandat]);

    const _onChangeNiveau = useCallback((event, option) => {
        const idNiveauSelectionne = option.key;
        if (idNiveauSelectionne === IDS_NIVEAU.OPERATION) _setLotsSelectionnes([]);
        _setIdNiveauSelectionne(idNiveauSelectionne);
        _setMandat(prev => {
            let mandat = { ...prev };
            mandat.mandatNational = idNiveauSelectionne === IDS_NIVEAU.NATIONAL;
            switch (idNiveauSelectionne) {
                case "0":
                case IDS_NIVEAU.NATIONAL: mandat.idRegion = 0;
                case IDS_NIVEAU.REGION: mandat.idAgence = 0;
                case IDS_NIVEAU.AGENCE: mandat.idOperation = 0;
                case IDS_NIVEAU.OPERATION:
                case IDS_NIVEAU.LOT:
                default: break;
            }
            return mandat;
        });
    }, []);

    const _onChangePerimetre = useCallback((key, value) => {
        _setLotsSelectionnes([]);
        _setMandat(prev => {
            let mandat = { ...prev, [key]: value };
            switch (key) {
                case "idRegion": mandat.idAgence = 0;
                case "idAgence": mandat.idOperation = 0;
                case "idOperation":
                default: break;
            }
            return mandat;
        });
    }, []);

    const _onChangeProduit = useCallback((event, option) => {
        _setLotsSelectionnes([]);
        _setMandat(prev => ({ ...prev, idProduit: option.key }));
    }, []);

    const _onChangeSelectionListAlerte = useCallback((items) => {
        if (!idMandat)
            _setListeDesAlertesSelectionnees(items);
    }, [idMandat]);

    const _onChangeTypeDeMandat = useCallback((event, option) => {
        const idTypeMandat = option.key;
        _setListeDesAlertesSelectionnees([]);
        _setMandat(prev => {
            let mandat = { ...prev, idTypeMandat };
            if (idTypeMandat === IDS_TYPE_MANDAT.REPARTITEUR) mandat.exclusivite = false;
            if (idTypeMandat === IDS_TYPE_MANDAT.EBOOK) mandat.idOperation = 0;
            return mandat;
        });
        if (idTypeMandat === IDS_TYPE_MANDAT.EBOOK) _setIdNiveauSelectionne(IDS_NIVEAU.AGENCE);
    }, []);

    const _onDismiss = useCallback(() => {
        onDismiss();
    }, [onDismiss]);

    const _onDismissed = useCallback(() => {
        _isLoaded.current = false;
        _setEstAdminNational(false);
        _setFormIsNotValid();
        _setIdNiveauSelectionne("0");
        _setListeDesAlertesSelectionnees([]);
        _setLotsSelectionnes([]);
        _setMandat(defaultMandat);
    }, []);

    const _onOpen = useCallback(() => {
        _chargementDonnees();
    }, [_chargementDonnees]);

    const _onSave = useCallback(() => {
        const id = load("Enregistrement du mandat en cours...");
        DataMandat._onEnregistrementMandat(_mandatObject, idMandat, _listeDesAlertesSelectionnees).then(response => {
            const onSuccess = onSave;
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [_listeDesAlertesSelectionnees, _mandatObject, idMandat, onSave]);

    const _onVerificationFormulaire = useCallback(() => {
        _setFormIsNotValid();
        Data._onValidationFormulaire("Mandat", _mandatObject, idMandat > 0 ? "Modification" : "Création").then(response => {
            const onSuccess = _setFormIsValid;
            const onError = (result) => extract(result, NotificationsTarget.panels);
            clear();
            Immocom.checkFetchResponse(response, onSuccess, onError);
        });
    }, [_mandatObject, idMandat]);

    // EFFECTS (ComponentDidUpdate)
    // Vérification des données du formulaire
    useEffect(() => {
        if (_isMounted.current && _isLoaded.current)
            _onVerificationFormulaire();
    }, [_isLoaded, _isMounted, JSON.stringify(_mandatObject)]);

    // EFFECTS (ComponentDidMount)
    useEffect(() => {
        _isMounted.current = true;
    }, []);

    // RENDER
    return <EUIPanel
        headerText={_headerText}
        isFormValid={_isFormValid}
        isOpen={isOpen}
        onDismiss={_onDismiss}
        onDismissed={_onDismissed}
        onOpen={_onOpen}
        onSave={_onSave}
        type={PanelType.extraLarge}
    >

        <div className="ms-Grid" dir="ltr">
            <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12 ms-xl6">

                    <div className="ms-Grid" dir="ltr">
                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                {
                                    typeDeMandat === 'Eiffage' ?
                                        <ComboBoxCompte
                                            disabled={!!idMandat}
                                            idSociete={1}
                                            onChange={(event, option) => _setMandat(prev => ({ ...prev, loginCompte: option.key }))}
                                            required
                                            selectedKey={_mandat.loginCompte}
                                        />
                                        :
                                        <ComboBoxSociete //YD : 20230201 : 2264
                                            disabled={!!idMandat}
                                            doitInclureEiffage={false}
                                            doitInclureExterne={false}
                                            onChange={(event, option) => _setMandat(prev => ({ ...prev, idSociete: option.key, loginCompte: undefined }))}
                                            required
                                            selectedKey={_mandat.idSociete}
                                        />
                                }
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <TextFieldHonoraires // KP (22/05/20020) : ticket 894
                                    disabled={_mandat.idTypeMandat !== IDS_TYPE_MANDAT.COMMERCIALISATEUR}
                                    onChange={_setFormIsNotValid}
                                    onDelayedChange={(event, newValue) => _setMandat(prev => ({ ...prev, honoraireCommercialisation: newValue }))}
                                    required={_mandat.idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR}
                                    value={_mandat.honoraireCommercialisation || 0}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <EUIDatePicker
                                    isRequired
                                    label="Mandat valable du"
                                    onSelectDate={date => _setMandat(prev => ({ ...prev, dateDebut: Scriba.date().getChaineISOFromDate(date, true) }))}
                                    value={_mandat.dateDebut}
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <EUIDatePicker
                                    isRequired
                                    label="Au"
                                    onSelectDate={date => _setMandat(prev => ({ ...prev, dateFin: Scriba.date().getChaineISOFromDate(date, true) }))}
                                    value={_mandat.dateFin}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <ComboBoxStatutMandat
                                    disabled // KP (20/05/2020) : ticket 895
                                    selectedKey={_mandat.idStatutMandat}
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <ComboBoxTypeMandat
                                    disabled={_isDisabledComboBoxTypeMandat}
                                    inclureAdministrateur={_estAdminNational}
                                    inclureCallCenter={_estAdminNational}
                                    inclureCommercialisateur={idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR}
                                    inclureControleDeGestionNational={_estAdminNational}
                                    inclureMaisonIndividuelle={_estAdminNational}
                                    inclureModuleExterne={_estAdminNational}
                                    inclureWebServices={idTypeMandat === IDS_TYPE_MANDAT.WEB_SERVICES}
                                    onChange={_onChangeTypeDeMandat}
                                    required={!_isDisabledComboBoxTypeMandat}
                                    selectedKey={_mandat.idTypeMandat}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 ms-lg3">
                                <ChoiceGroup
                                    disabled={_mandat.idTypeMandat !== IDS_TYPE_MANDAT.COMMERCIALISATEUR}
                                    label="Base cal. hon."
                                    onChange={(event, option) => _setMandat(prev => ({ ...prev, baseCalculHonoraireCommercialisation: option.key }))}
                                    options={[
                                        { key: "HT sur HT", text: "HT sur HT" }, //MG: 2335
                                        { key: "HT sur TTC", text: "HT sur TTC" },
                                        { key: "TTC sur TTC", text: "TTC sur TTC" }
                                    ]}
                                    required={_mandat.idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR}
                                    selectedKey={_mandat.baseCalculHonoraireCommercialisation} //MG: 2335
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg3">
                                <EUINumericField
                                    disabled={_mandat.idTypeMandat !== IDS_TYPE_MANDAT.COMMERCIALISATEUR && _mandat.idTypeMandat !== IDS_TYPE_MANDAT.WEB_SERVICES}
                                    label="Nb opt. simul. /op."
                                    min={0}
                                    onChange={_setFormIsNotValid}
                                    onDelayedChange={(event, newValue) => _setMandat(prev => ({ ...prev, nbreOptionParOperation: newValue || 0 }))}
                                    required={_mandat.idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR || _mandat.idTypeMandat === IDS_TYPE_MANDAT.WEB_SERVICES}
                                    value={_mandat.nbreOptionParOperation}
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg3">
                                <EUINumericField
                                    disabled={_mandat.idTypeMandat !== IDS_TYPE_MANDAT.COMMERCIALISATEUR && _mandat.idTypeMandat !== IDS_TYPE_MANDAT.WEB_SERVICES}
                                    label="Durée d'option"
                                    min={0}
                                    onChange={_setFormIsNotValid}
                                    onDelayedChange={(event, newValue) => _setMandat(prev => ({ ...prev, dureeOption: newValue || 0 }))}
                                    required={_mandat.idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR || _mandat.idTypeMandat === IDS_TYPE_MANDAT.WEB_SERVICES}
                                    suffix="jour(s)"
                                    value={_mandat.dureeOption}
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg3">
                                <EUIToggle
                                    checked={_mandat.optionRenouvelable}
                                    disabled={_mandat.idTypeMandat !== IDS_TYPE_MANDAT.COMMERCIALISATEUR}
                                    label="Opt. ren. (1 fois)"
                                    onChange={(event, isChecked) => _setMandat(prev => ({ ...prev, optionRenouvelable: isChecked }))}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12">
                                <DetailsListNiveauAlerte
                                    activeItems={_listeDesAlertesSelectionnees}
                                    idTypeMandat={_mandat.idTypeMandat}
                                    onSelectionChanged={_onChangeSelectionListAlerte}
                                    selectionMode={!!idMandat ? SelectionMode.none : SelectionMode.multiple}
                                    wrapInScrollablePane={false}
                                />
                            </div>
                        </div>
                    </div>

                </div>
                <div className="ms-Grid-col ms-sm12 ms-xl6">

                    <div className="ms-Grid" dir="ltr">
                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <EUIToggle
                                    checked={_mandat.mandatToutProduit}
                                    disabled
                                    label="Tout produit"
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <ComboBoxProduit
                                    disabled={_isDisabledComboBoxProduit}
                                    onChange={_onChangeProduit}
                                    required={!_isDisabledComboBoxProduit}
                                    selectedKey={_mandat.idProduit}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <EUIToggle
                                    checked={_mandat.exclusivite}
                                    disabled={!!idMandat || _mandat.idTypeMandat === IDS_TYPE_MANDAT.REPARTITEUR} //MG - 2366
                                    label="Allotement"
                                    onChange={(event, isChecked) => _setMandat(prev => ({ ...prev, exclusivite: isChecked }))}
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg6">
                                <ComboBoxNiveau
                                    disabled={!!idMandat} //non modifiable en mode update
                                    inclureLot={_mandat.idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR || _mandat.idTypeMandat === IDS_TYPE_MANDAT.WEB_SERVICES}
                                    inclureOperation={_mandat.idTypeMandat !== IDS_TYPE_MANDAT.EBOOK}
                                    inclureProgramme={false}
                                    onChange={_onChangeNiveau}
                                    required
                                    selectedKey={_idNiveauSelectionne}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12">
                                <ComboBoxRegion
                                    disabled={!!idMandat || !_idNiveauSelectionne || _idNiveauSelectionne === "0" || _idNiveauSelectionne === NATIONAL}
                                    onChange={(event, option) => _onChangePerimetre("idRegion", option.key)}
                                    required={_idNiveauSelectionne === REGION || _idNiveauSelectionne === AGENCE || _idNiveauSelectionne === OPERATION || _idNiveauSelectionne === LOT}
                                    selectedKey={_mandat.idRegion}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12">
                                <ComboBoxAgence
                                    disabled={!!idMandat || !_idNiveauSelectionne || _idNiveauSelectionne === NATIONAL || _idNiveauSelectionne === REGION}
                                    idRegion={_mandat.idRegion}
                                    onChange={(event, option) => _onChangePerimetre("idAgence", option.key)}
                                    required={_idNiveauSelectionne === AGENCE || _idNiveauSelectionne === OPERATION || _idNiveauSelectionne === LOT}
                                    selectedKey={_mandat.idAgence}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12">
                                <ComboBoxOperation
                                    disabled={!!idMandat || !_idNiveauSelectionne || _idNiveauSelectionne === NATIONAL || _idNiveauSelectionne === REGION || _idNiveauSelectionne === AGENCE}
                                    idAgence={_mandat.idAgence}
                                    idRegion={_mandat.idRegion}
                                    onChange={(event, option) => _onChangePerimetre("idOperation", option.key)}
                                    perimetre="eiffage"
                                    required={_idNiveauSelectionne === OPERATION || _idNiveauSelectionne === LOT}
                                    selectedKey={_mandat.idOperation}
                                />
                            </div>
                        </div>

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12">
                                {(_mandat.idTypeMandat === IDS_TYPE_MANDAT.COMMERCIALISATEUR || _mandat.idTypeMandat === IDS_TYPE_MANDAT.WEB_SERVICES) && _idNiveauSelectionne === LOT &&
                                    <Fragment>
                                        <Label required>Lots commerciaux</Label>
                                        <DetailsListLot
                                            activeItems={_lotsSelectionnes}
                                            idOperation={_mandat.idOperation}
                                            onSelectionChanged={items => _setLotsSelectionnes(items)}
                                            scrollablePaneStyles={{ root: { height: "34vh" } }}
                                        />
                                    </Fragment>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {!!idMandat &&
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                        Créé le {Scriba.date().stringDateFormatCourt(_mandat.creerLe)} par {_mandat.creerPar}
                    </div>
                    <div className="ms-Grid-col ms-sm12">
                        Modifié le {Scriba.date().stringDateFormatCourt(_mandat.modifierLe)} par {_mandat.modifierPar}
                    </div>
                </div>
            }
        </div>

    </EUIPanel>;

};

export default FicheMandat;